// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { CanvasRect } from 'flow/store/redux/types';
import type {
  Graph,
} from '../types';

import type { WorkflowEdge } from '../edges';
import {
  computeEdgeState, getEdgeZIndex,
} from '../edges';

import {
  CANVAS_VERTICAL_PADDING,
  NODE_WIDTH,
} from './constants';
import type {
  NodeLineage,
  BidirectionalGraphTopology,
  AdjacencyMap,
  MaxPathLengthMap,
} from './types';
import type { WorkflowNode } from '../nodes';
import {
  getLayoutContainerId,
  getNodeZIndex,
  indexWorkflowNodes,
  isContainerNode,
  isExpandedSnippetNode,
  isFolderNode,
  isSnippetInternalChildNode,
} from '../nodes';
import { sortGraphBeforePlacement } from './sorting/sorting';
import type { PublicId } from 'flow/store/engine/types';
import { isActionIdOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import type { NodeDimensions, NodePosition } from './nodeMatrix';
import {
  allocate,
  allocateSubgraphOwnership,
  createDefaultNodeMatrix,
  defaultNodeDimensions,
  deriveXYPositions,
  getNextAvailableRowIndex,
  getNodeColumn,
  getNodePosition,
  getSnippetNodeDimensions,
} from './nodeMatrix';

/**
 * builds a Map mapping a node id and the maximum number of nodes it has connected to it
 * @param incomers Map containing index of node ids and the ids of nodes connected to it
 * @param snippetConfig Optional config for handling snippet state path adjustments
 * @returns Map with node id to depth relationship
 */
export function getMaxPathLengthMap(args: {
  incomers: AdjacencyMap;
  snippetConfig?: {
    indexedNodes: Record<WorkflowNode['id'], WorkflowNode>;
    snippetDimensions: Record<PublicId, NodeDimensions> ;
  };
}): MaxPathLengthMap {
  const { incomers, snippetConfig } = args;
  const maxPathLengthMap: MaxPathLengthMap = new Map();

  function traverseDependencies(id: string) {
    if (maxPathLengthMap.has(id)) {
      return maxPathLengthMap.get(id);
    }

    const dependenciesForNodeId = incomers.get(id);

    if (!dependenciesForNodeId?.size) {
      const result = 0;
      maxPathLengthMap.set(id, result);
      return result;
    }

    let maxPathLengthForNodeId = -Infinity;
    for (const dependencyId of Array.from(dependenciesForNodeId.values())) {
      if (snippetConfig) {
        const { indexedNodes, snippetDimensions } = snippetConfig;
        const isSnippet = isActionIdOfType(dependencyId, ActionType.SNIPPET);
        if (isSnippet) {
          // for internal nodes, the snippet does not offset the max path
          // for external nodes, the path is offset by the snippet's width
          const node = indexedNodes[id];
          const snippetOffset = isSnippetInternalChildNode(node)
            ? 0
            : snippetDimensions[dependencyId]?.nodeWidth || 0;

          const result = traverseDependencies(dependencyId) + snippetOffset;
          maxPathLengthForNodeId = Math.max(maxPathLengthForNodeId, result);
        }
      }

      const result = traverseDependencies(dependencyId) + 1;

      maxPathLengthForNodeId = Math.max(maxPathLengthForNodeId, result);
    }
    maxPathLengthMap.set(id, maxPathLengthForNodeId);
    return maxPathLengthForNodeId;
  }

  for (const nodeId of Array.from(incomers.keys())) {
    traverseDependencies(nodeId);
  }

  return maxPathLengthMap;
}


/**
 * Builds a topology bundle mapping all node ids to their direct incomer and outgoer node ids.
 * @param graph Graph with nodes and edges
 * @param nodeContainerState Node folder & Snippet collapsed states
 * @returns a bundle with incomer and outgoer connections per node
 */
export function getBidirectionalGraphTopology(
  graph: Graph,
  nodeContainerState: WorkflowState['ui']['nodeContainers'],
): BidirectionalGraphTopology {
  // maps a nodeId to all nodeIds that connect to that nodeId
  const incomers: AdjacencyMap = new Map();
  // maps a nodeId to all nodeIds that the nodeId connect to
  const outgoers: AdjacencyMap = new Map();
  const { inputAttribute: nodeFolderState, snippets: snippetCollapsedState } = nodeContainerState;


  // index the nodes into a Map by their ID for a faster lookup
  const nodesIndexedById = graph.nodes.reduce((indexedNodes, node) => {
    indexedNodes.set(node.id, node);
    if (isContainerNode(node)) {
      const state = isFolderNode(node) ? nodeFolderState : snippetCollapsedState[node.id];
      if (state.isCollapsed) {
        // add container children to topology if the container is collapsed
        node.data.children.forEach(child => indexedNodes.set(child.id, child));
      }
    }
    return indexedNodes;
  }, new Map<WorkflowNode['id'], WorkflowNode>());

  /**
   * we build 2 data structures of the same type (AdjacencyMap) in a single pass;
   * one for incomer connections (connection _to_ a node) and another for outgoer
   * connections (connections _from_ a node).
   */
  graph.edges.forEach(({ source: sourceId, target: targetId }) => {
    if (nodesIndexedById.get(sourceId)?.data?.layout.asIncomer) {
      if (!incomers.has(targetId)) {
        incomers.set(targetId, new Set());
      }
      incomers.get(targetId).add(sourceId);
    }
    if (nodesIndexedById.get(targetId)?.data?.layout.asOutgoer) {
      if (!outgoers.has(sourceId)) {
        outgoers.set(sourceId, new Set());
      }
      outgoers.get(sourceId).add(targetId);
    }
  });

  return {
    incomers,
    outgoers,
  };
}

/**
 * @param graph graph of nodes and edges
 * @param node source node from which to find all connected node ids
 * @returns object containing Sets of Node ids organized by their ancestry relationship to the Node
 */
export function getAllNodeIdsConnectedToNode(
  nodeId: WorkflowNode['id'], graphTopology: BidirectionalGraphTopology,
): NodeLineage {
  if (!nodeId) {
    return {
      nodeId: null,
      directIncomers: new Set(),
      directOutgoers: new Set(),
      extendedIncomers: new Set(),
      extendedOutgoers: new Set(),
      all: new Set(),
    };
  }

  const directIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const directOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const allConnectedNodeIdsSet: Set<WorkflowNode['id']> = new Set();


  // recurse up the node incomers and append their ids
  function appendIncomerNodeIds(_nodeId: WorkflowNode['id']) {
    const incomerNodeIdsForNode = graphTopology.incomers.get(_nodeId);
    if (!incomerNodeIdsForNode) {
      return;
    }

    for (const incomerNodeId of Array.from(incomerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directIncomerIdsSet.add(incomerNodeId);
      } else {
        extendedIncomerIdsSet.add(incomerNodeId);
      }
      allConnectedNodeIdsSet.add(incomerNodeId);
      appendIncomerNodeIds(incomerNodeId);
    }
  }

  // recurse down the node outgoers and append their ids
  function appendOutgoerNodeIds(_nodeId: WorkflowNode['id']) {
    const outgoerNodeIdsForNode = graphTopology.outgoers.get(_nodeId);
    if (!outgoerNodeIdsForNode) {
      return;
    }

    for (const outgoerNodeId of Array.from(outgoerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directOutgoersIdsSet.add(outgoerNodeId);
      } else {
        extendedOutgoersIdsSet.add(outgoerNodeId);
      }
      allConnectedNodeIdsSet.add(outgoerNodeId);
      appendOutgoerNodeIds(outgoerNodeId);
    }
  }

  allConnectedNodeIdsSet.add(nodeId);
  appendIncomerNodeIds(nodeId);
  appendOutgoerNodeIds(nodeId);

  return {
    nodeId,
    directIncomers: directIncomerIdsSet,
    directOutgoers: directOutgoersIdsSet,
    extendedIncomers: extendedIncomerIdsSet,
    extendedOutgoers: extendedOutgoersIdsSet,
    all: allConnectedNodeIdsSet,
  };
}


/**
 * Split out the nodes in a graph by their layout mode
 * @param graph graph of nodes and edges
 * @returns object containing all nodes split in two categories: auto and manual layout modes
 */
export function splitGraphNodesByLayoutMode(graph: Graph): {
  autoLayoutNodes: WorkflowNode[]; manualLayoutNodes: WorkflowNode[];
} {
  const splitNodes = graph.nodes.reduce((_splitNodes, node) => {
    if (!node.data || node.data.layout.autoLayout) {
      _splitNodes.autoLayoutNodes.push(node);
    } else {
      _splitNodes.manualLayoutNodes.push(node);
    }


    return _splitNodes;
  }, { autoLayoutNodes: [], manualLayoutNodes: [] });


  return splitNodes;
}

/**
 * Adjusts the position of the elements that need to stay in place as the user pans and zooms the canvas.
 */
export function adjustLayoutToFreezeFixedNodes(graph: Graph, canvasRect: CanvasRect) {
  graph.nodes.forEach(node => {
    if (node.data?.layout.fixed) {
      const { x: currentX, y: currentY } = node.position;

      node.position = {
        x: (currentX - canvasRect.x) / canvasRect.zoom,
        y: (currentY - canvasRect.y) / canvasRect.zoom,
      };
    }
  });
}

/**
 * moves the outcome construction nodes up top to create the connection effect
 */
export function adjustOutcomeConstructionNodes(graph: Graph, focusedNodeId: WorkflowState['ui']['focusedNodeId']) {
  const focusedNode = graph.nodes.find(node => node.id === focusedNodeId);

  if (focusedNode?.type === 'outcome') {
    focusedNode.position = {
      ...focusedNode.position,
      y: CANVAS_VERTICAL_PADDING,
    };
  }
}

function isInternalSnippetEdge(
  edge: WorkflowEdge,
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>,
): boolean {
  const sourceNode = indexedNodes[edge.source];
  const targetNode = indexedNodes[edge.target];

  return (
    isSnippetInternalChildNode(sourceNode)
      && isSnippetInternalChildNode(targetNode)
      && getLayoutContainerId(sourceNode) === getLayoutContainerId(targetNode)
  );
}


/**
 * this is the entry function for the lay-outing logic.
 * set the position of the existing nodes in the graph
 */
export function layOutGraph({
  graph,
  uiStatePartial,
  nodeLineage,
  maxIncomingPathLengthMap,
  subGraphDimensions,
  hasInputAttributeNodeFolder,
}: {
  graph: Graph;
  uiStatePartial: {
    activeNodeEditorTab: WorkflowState['ui']['activeNodeEditorTab'];
    canvasWidth: WorkflowState['ui']['canvasProperties']['rect']['width'];
    floatedAnchorNodeId: WorkflowState['ui']['floatedAnchorNodeId'];
    focusedNodeId: WorkflowState['ui']['focusedNodeId'];
  };
  nodeLineage: NodeLineage;
  maxIncomingPathLengthMap: MaxPathLengthMap;
  subGraphDimensions: Record<PublicId, NodeDimensions>;
  hasInputAttributeNodeFolder?: boolean;
}): void {
  /**
   * sorting the nodes prior to their placement allows us to customize the order in which
   * they are placed in the graph without making changes to the positional algorithm
  */
  sortGraphBeforePlacement(
    graph,
    uiStatePartial,
    nodeLineage,
  );

  const {
    autoLayoutNodes,
    manualLayoutNodes,
  } = splitGraphNodesByLayoutMode(graph);

  const indexedNodes = indexWorkflowNodes(graph.nodes);

  // we auto layout nodes in three steps:
  // 1. allocate ownership/space for subgraphs within the matrix
  // 2. populate the matrix with nodes
  // 3. derive (x,y) canvas positions for each node

  // memoize the count of nodes per column (array index is column number)
  const nodeMatrix = createDefaultNodeMatrix({
    nodeHeight: graph.nodes.length,
    nodeWidth: graph.nodes.length,
  });

  // a map from node id -> node's position in the node matrix (column, row)
  const nodeMatrixLocationMap: Record<WorkflowNode['id'], NodePosition> = {};

  allocateSubgraphOwnership({
    matrix: nodeMatrix,
    nodes: autoLayoutNodes,
    subGraphDimensions,
    maxIncomingPathLengthMap,
  });

  autoLayoutNodes.forEach(node => {
    const columnIndex = getNodeColumn(
      maxIncomingPathLengthMap,
      node,
    );
    const column = nodeMatrix[columnIndex];
    const rowIndex = getNextAvailableRowIndex(column, node);

    nodeMatrixLocationMap[node.id] = { column: columnIndex, row: rowIndex };

    const dimensions = isExpandedSnippetNode(node)
      ? getSnippetNodeDimensions(subGraphDimensions[node.id])
      : defaultNodeDimensions;

    if (rowIndex !== -1) {
      allocate({
        matrix: nodeMatrix,
        position: { column: columnIndex, row: rowIndex },
        dimensions,
        nodeId: node.id,
      });
    }
  });

  deriveXYPositions({
    matrix: nodeMatrix,
    indexedNodes,
    hasInputAttributeNodeFolder,
  });

  autoLayoutNodes.forEach(node => {
    // guard this for now.
    // during merge, we sometimes are unable to allocate space for snippet children
    // due to an interim state where we first add snippet children (nodes)
    // and THEN build the snippet dependencies (edges).
    // Layouting can be triggered before the correct edges are added.
    if (nodeMatrixLocationMap[node.id]) {
      const { row, column } = nodeMatrixLocationMap[node.id];
      if (row > -1 && column > -1) {
        const { position } = nodeMatrix[column][row];
        node.position = position;
        node.zIndex = getNodeZIndex(node);
      }
    } else {
      node.position = { x: 0, y: 0 };
      node.zIndex = getNodeZIndex(node);
    }
  });

  /**
   * Manually place nodes that need to be
   */
  let precedenceIndexedNodeCount = 0;
  manualLayoutNodes.forEach(node => {
    /**
     * since outcomes and workflow routing nodes are sorted by precedence, we can use their local
     * index as their row. the column pixels (y) are manually computed by taking in the value of
     * the canvas width. this places them outside of the visible canvas, flush to its right
     * edge.
     */
    if (node.type === 'outcome' || node.type === 'workflow') {
      const x = uiStatePartial.canvasWidth;
      // we don't care about the column so we pass a `0`
      const { y } = getNodePosition({ row: precedenceIndexedNodeCount, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex(node);
      precedenceIndexedNodeCount += 1;
    }

    /**
     * importedTailNodes can be placed at the top row and far away into the left edge to
     * accomplish the desired effect without having to manually align them to their corresponding
     * imported node
     */
    if (node.type === 'importedTailNode') {
      const x = 0 - (2 * NODE_WIDTH);
      const { y } = getNodePosition({ row: 0, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex(node);
    }
  });

  graph.edges.forEach(edge => {
    const edgeState = computeEdgeState({
      sourceId: edge.source,
      targetId: edge.target,
      nodeLineage,
      uiStatePartial,
    });

    edge.zIndex = getEdgeZIndex(
      edgeState,
      isInternalSnippetEdge(edge, indexedNodes),
    );
  });
}
